import { CheckboxWithToolTip } from 'components/WithTooltip';
import type {
    BimWorkOrderChangePriceData,
    BimWorkOrderChangeTotalItemData,
    WorkOrderChangeStatusEnum,
} from 'gql/graphql';
import { Checkbox, Loader, IconExpand, Button } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import { BimWorkOrderRowTypeEnum, BimWorkOrderRowDetailsType } from '../../../WorkOrder/types';

import { WorkOrderChangePriceContextMenu } from './WorkOrderChangePriceContextMenu';

export type TDetailsData = {
    rowType: BimWorkOrderRowTypeEnum;
    detailsType: BimWorkOrderRowDetailsType;
    data: BimWorkOrderChangePriceData | TBottomRow;
    prevCheckedPrice?: TDetailsData | null;
};

type TBottomRow = BimWorkOrderChangeTotalItemData & { isTotalRow?: boolean; topPinnedRow?: boolean };

interface IMenuRendererProps extends ICellRendererParams<BimWorkOrderChangePriceData | TBottomRow> {
    checkedRows: Record<string, boolean>;
    checkRowHandler: (id: string, state: BimWorkOrderChangePriceData | null) => void;
    isActionDisabled: boolean;
    openDetailsHandler: ({ rowType, detailsType, data }: TDetailsData) => void;
    refreshTable: () => void;
    isMenuVisible?: boolean;
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
}

export const BimWorkOrderChangeMenuRenderer = (props: IMenuRendererProps) => {
    const {
        data,
        checkRowHandler,
        checkedRows,
        isActionDisabled = true,
        openDetailsHandler,
        isMenuVisible,
        refreshTable,
        workOrderChangeStatus,
    } = props;

    const isBottomPinnedRow = useMemo(() => data && 'isTotalRow' in data && data?.isTotalRow, [data]);
    const isTopPinnedRow = useMemo(() => data && 'topPinnedRow' in data && data?.topPinnedRow, [data]);

    const handleCheckbox = useCallback(
        (id: string) =>
            ({ checked }: { checked: boolean }) => {
                if (checked && data && 'workOrderPriceId' in data) {
                    checkRowHandler(id, data);
                } else {
                    checkRowHandler(id, null);
                }
            },
        [checkRowHandler, data],
    );

    const deletionDisabled = useMemo(
        () => isActionDisabled || Boolean(data && 'canRemoved' in data && !data?.canRemoved),
        [data, isActionDisabled],
    );

    const openDetailsResolver = useCallback(
        (detailsType: BimWorkOrderRowDetailsType) => {
            if (data) {
                if (isBottomPinnedRow) {
                    openDetailsHandler({ rowType: BimWorkOrderRowTypeEnum.TotalsString, detailsType, data });
                } else {
                    openDetailsHandler({ rowType: BimWorkOrderRowTypeEnum.RegularString, detailsType, data });
                }
            }
        },
        [data, isBottomPinnedRow, openDetailsHandler],
    );

    const openRowDetails = useCallback(() => {
        openDetailsResolver(BimWorkOrderRowDetailsType.Details);
    }, [openDetailsResolver]);

    if (!data) {
        return <Loader />;
    }

    if ('isLimitPrice' in data && data?.isLimitPrice) {
        return '';
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {isMenuVisible &&
                !isBottomPinnedRow &&
                (deletionDisabled && (data as BimWorkOrderChangePriceData)?.hasActKC2 ? (
                    <CheckboxWithToolTip
                        checkboxProps={{ checked: false }}
                        tooltipProps={{
                            mode: 'mouseover',
                            content: 'В составе сметы загружены Акты КС-2. Удаление недоступно',
                        }}
                        fakeDisabled
                    />
                ) : (
                    <Checkbox
                        checked={
                            checkedRows[
                                `${(data as BimWorkOrderChangePriceData).workOrderPriceId}_${
                                    (data as BimWorkOrderChangePriceData).workOrderChangePriceId
                                }`
                            ]
                        }
                        onChange={handleCheckbox(
                            `${(data as BimWorkOrderChangePriceData).workOrderPriceId}_${
                                (data as BimWorkOrderChangePriceData).workOrderChangePriceId
                            }`,
                        )}
                        disabled={deletionDisabled}
                    />
                ))}

            {isMenuVisible && !isBottomPinnedRow && (
                <WorkOrderChangePriceContextMenu
                    data={data as BimWorkOrderChangePriceData}
                    openDetails={openDetailsResolver}
                    refreshTable={refreshTable}
                    workOrderChangeStatus={workOrderChangeStatus}
                />
            )}
            {!isTopPinnedRow && (
                <Button iconLeft={IconExpand} onClick={openRowDetails} onlyIcon view="clear" size="xs" />
            )}
        </div>
    );
};

BimWorkOrderChangeMenuRenderer.displayName = 'BimWorkOrderChangeMenuRenderer';
